import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { createSalesperson } from "@/Calls/callDealer"; // reuse the same API call until you add salesmanager endpoint

const SalesManagerSchema = z.object({
    salesmanager_firstname: z.string().min(2, "First name required"),
    salesmanager_lastname: z.string().min(2, "Last name required"),
    salesmanager_email: z.email("Invalid email"),
    salesmanager_password: z.string().min(6, "Password must be at least 6 chars"),
    salesmanager_mobilephone_number: z.string().optional(),
    salesmanager_job_title: z.string().optional(),
});

type SalesManagerForm = z.infer<typeof SalesManagerSchema>;

export default function SalesManagerCreatePage() {
    const form = useForm<SalesManagerForm>({
        resolver: zodResolver(SalesManagerSchema),
        defaultValues: {
            salesmanager_firstname: "",
            salesmanager_lastname: "",
            salesmanager_email: "",
            salesmanager_password: "",
            salesmanager_mobilephone_number: "",
            salesmanager_job_title: "Sales Manager",
        },
    });

    const [submitting, setSubmitting] = React.useState(false);

    async function onSubmit(values: SalesManagerForm) {
        try {
            setSubmitting(true);

            // 🔧 Build payload aligned with backend model (reusing salesperson endpoint for now)
            const payload = {
                salesperson_firstname: values.salesmanager_firstname,
                salesperson_lastname: values.salesmanager_lastname,
                salesperson_email: values.salesmanager_email,
                salesperson_password: values.salesmanager_password,
                salesperson_mobilephone_number: values.salesmanager_mobilephone_number,
                salesperson_job_title: values.salesmanager_job_title || "Sales Manager",
            };

            await createSalesperson(payload);

            toast("✅ Sales Manager created", {
                description: `${values.salesmanager_firstname} ${values.salesmanager_lastname} has been added successfully.`,
                action: {
                    label: "View Staff",
                    onClick: () => window.location.assign("/dealer/staff"),
                },
            });

            form.reset();
        } catch (err) {
            console.error(err);
            const message =
                err instanceof Error ? err.message : "Unexpected error — please try again later.";
            toast("❌ Error creating Sales Manager", { description: message });
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="container mx-auto max-w-2xl py-8">
            <Card>
                <CardHeader>
                    <CardTitle>Add New Sales Manager</CardTitle>
                </CardHeader>
                <CardContent>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="salesmanager_firstname"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>First Name</FormLabel>
                                            <FormControl>
                                                <Input {...field} placeholder="John" />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="salesmanager_lastname"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Last Name</FormLabel>
                                            <FormControl>
                                                <Input {...field} placeholder="Doe" />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <FormField
                                control={form.control}
                                name="salesmanager_email"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Email</FormLabel>
                                        <FormControl>
                                            <Input {...field} type="email" placeholder="email@dealer.com" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="salesmanager_password"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Password</FormLabel>
                                        <FormControl>
                                            <Input {...field} type="password" placeholder="********" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="salesmanager_mobilephone_number"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Mobile Phone</FormLabel>
                                        <FormControl>
                                            <Input {...field} placeholder="(555) 555-5555" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="salesmanager_job_title"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Job Title</FormLabel>
                                        <FormControl>
                                            <Input {...field} placeholder="Sales Manager" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <Button type="submit" disabled={submitting} className="w-full">
                                {submitting ? "Creating..." : "Create Sales Manager"}
                            </Button>
                        </form>
                    </Form>
                </CardContent>
            </Card>
        </div>
    );
}
